import { Building2, Check, ChevronsUpDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { TenantResponse } from '@/api/responses'
import { cn } from '@/lib/utils'

import { useDisclosure } from './useDisclosure'

export interface TenantSwitcherProps {
  readonly tenants: readonly TenantResponse[]
  readonly currentTenant: TenantResponse
  readonly onSelect: (tenant: TenantResponse) => void
}

/**
 * Troca de organizacao, separada do menu do usuario (docs/design/02-INFORMATION-ARCHITECTURE.md
 * secao 7). So mostra o seletor quando o usuario tem mais de uma organizacao.
 */
export function TenantSwitcher({ tenants, currentTenant, onSelect }: TenantSwitcherProps) {
  const { t } = useTranslation()
  const { isOpen, setIsOpen, containerRef } = useDisclosure()

  if (tenants.length <= 1) {
    return (
      <div className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-sm text-foreground-primary">
        <Building2 aria-hidden="true" className="h-4 w-4 shrink-0 text-foreground-muted" />
        <span className="truncate font-medium">{currentTenant.name}</span>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative min-w-0 flex-1">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t('shell.tenantSwitcher.openLabel')}
        onClick={() => {
          setIsOpen((value) => !value)
        }}
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground-primary hover:bg-background-elevated focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
      >
        <Building2 aria-hidden="true" className="h-4 w-4 shrink-0 text-foreground-muted" />
        <span className="min-w-0 flex-1 truncate text-left font-medium">{currentTenant.name}</span>
        <ChevronsUpDown aria-hidden="true" className="h-4 w-4 shrink-0 text-foreground-muted" />
      </button>
      {isOpen ? (
        <ul
          role="menu"
          aria-label={t('shell.tenantSwitcher.ariaLabel')}
          className="absolute left-0 z-10 mt-1 w-56 rounded-md border border-border-default bg-background-elevated py-1 shadow-sm"
        >
          {tenants.map((tenant) => {
            const isCurrent = tenant.id === currentTenant.id

            return (
              <li key={tenant.id} role="none">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={isCurrent}
                  onClick={() => {
                    setIsOpen(false)

                    if (!isCurrent) {
                      onSelect(tenant)
                    }
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground-primary hover:bg-background-subtle"
                >
                  <Check
                    aria-hidden="true"
                    className={cn(
                      'h-4 w-4 shrink-0 text-action-primary',
                      isCurrent ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{tenant.name}</span>
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
